<template>
	<v-card
		:loading="loadingWorkspace"
		:width="displayResponsive ? '100%' : '25em'"
		color="contained"
		rounded="xl"
		style="margin: 0 auto"
		@click="click"
	>
		<div class="d-flex justify-center">
			<nta-cube :size="displayResponsive ? 150 : 100" :active="['operator']" />
		</div>
		<div class="d-flex justify-space-between align-center">
			<v-card-title style="max-width: 100%">
				<span style="white-space: pre-wrap">{{ term }} </span>
			</v-card-title>
		</div>

		<v-card-text style="padding-top: 0">
			<div v-if="props.stats" style="color: rgb(var(--v-theme-primary))">
				<v-row>
					<v-col cols="4">
						<v-card-subtitle style="padding-left: 0px; padding-right: 8px">Memory</v-card-subtitle>
						<v-card-text style="padding: 8px; padding-left: 0px">{{ props.stats.memoryMB }} MB</v-card-text>
					</v-col>
					<v-col cols="3">
						<v-card-subtitle style="padding-left: 0px; padding-right: 8px">CPU</v-card-subtitle>
						<v-card-text style="padding: 8px; padding-left: 0px"
							>{{ props.stats.cpuUsagePercent }} %</v-card-text
						>
					</v-col>
					<v-col cols="5">
						<v-card-subtitle style="padding-left: 0px; padding-right: 8px">Uptime</v-card-subtitle>
						<v-card-text style="padding: 8px; padding-left: 0px">{{ formattedUptime }}</v-card-text>
					</v-col>
				</v-row>
			</div>
			{{ description }}
		</v-card-text>
		<div style="position: absolute; top: 5px; right: 5px">
			<v-btn
				v-if="debugStore.altKeyHeld && domainStore.hasAccessRights(UserRole.Editor)"
				icon="mdi-delete"
				color="error"
				variant="outlined"
				@click.stop="removeWorkspace"
			></v-btn>
			<v-btn
				v-if="!debugStore.altKeyHeld && domainStore.hasAccessRights(UserRole.Editor)"
				icon="mdi-pencil"
				variant="outlined"
				@click="editClick"
			></v-btn>
		</div>
	</v-card>
	<workspace-edit-modal
		:show="editWorkspaceModal"
		mode="edit"
		:workspace-id="workspaceId"
		:loading="loadingDone"
		@save="editDone"
		@remove="removeWorkspace"
		@done="() => (editWorkspaceModal = false)"
	></workspace-edit-modal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, PropType } from "vue";
import WorkspaceEditModal from "./WorkspaceEditModal.vue";
import type { ChildProcessStats, InstanceWorkspace } from "@lr-notitia/notitia-operator/schemas";
import { useInstanceWorkspacesStore } from "../../../store/i-workspace";
import { useDomainStore } from "../../../store/domain";
import { useInstanceDataspacesStore } from "../../../store/i-dataspace";
import { useDebugStore } from "../../../store/debug";
import { UserRole } from "@lr-notitia/notitia-operator/schemas";
import { useResponsiveLayout } from "@lr-notitia/notitia-ui";

let workspacesStore = useInstanceWorkspacesStore();
let domainStore = useDomainStore();
let debugStore = useDebugStore();
let instanceDataspacesStore = useInstanceDataspacesStore();

const props = defineProps({
	workspaceId: {
		type: String,
		required: true,
	},
	stats: {
		type: Object as PropType<ChildProcessStats>,
		required: false,
	},
});
const emits = defineEmits(["click"]);

const { displayResponsive } = useResponsiveLayout();

const workspace = computed(() => {
	return workspacesStore.getById(props.workspaceId);
});
const editWorkspaceModal = ref(false);
const loadingWorkspace = ref(false);
const loadingDone = ref(false);

const formattedUptime = computed(() => {
	if (!props.stats) return "";
	const seconds = Number(props.stats.uptimeSeconds);
	const h = Math.floor(seconds / 3600);
	const m = Math.floor((seconds % 3600) / 60);
	const s = (seconds % 60).toFixed(0);
	return `${h}h ${m}m ${s}s`;
});

async function click() {
	loadingWorkspace.value = true;
	if (!workspace.value) {
		await workspacesStore.loadWorkspace(domainStore.domain.id, props.workspaceId);
	}
	loadingWorkspace.value = false;
	emits("click", workspace.value?.id);
}
async function editClick(e: MouseEvent) {
	e.stopPropagation();
	editWorkspaceModal.value = true;
}
function editDone(d: InstanceWorkspace, name: string, description: string) {
	if (!domainStore.hasAccessRights(UserRole.Editor)) return;
	loadingDone.value = true;
	workspacesStore
		.updateWorkspace({
			...d,
			label: name,
			description: description,
		})
		.then((d) => {
			loadingDone.value = false;
			editWorkspaceModal.value = false;
		});
}
function removeWorkspace() {
	if (!domainStore.hasAccessRights(UserRole.Editor)) return;
	workspacesStore.removeWorkspace(props.workspaceId).catch((err) => console.error(err));
	let dataspace = instanceDataspacesStore.openedDataspace;
	if (!dataspace) return;
	instanceDataspacesStore.updateDataspace({
		...dataspace,
		workspaces: dataspace.workspaces.filter((w) => w != props.workspaceId),
	});
	workspacesStore.deleteWorkspaceDescriptor(domainStore.domain.id, dataspace.id, props.workspaceId);
}

const term = computed(() => {
	if (!instanceDataspacesStore.openedDataspaceId) return;
	return (
		workspacesStore.workspaceDescriptors[instanceDataspacesStore.openedDataspaceId]?.[props.workspaceId]?.label ||
		"New Workspace"
	);
});
const description = computed(() => {
	if (!instanceDataspacesStore.openedDataspaceId) return;
	return (
		workspacesStore.workspaceDescriptors[instanceDataspacesStore.openedDataspaceId]?.[props.workspaceId]
			?.description || new Date().toLocaleString()
	);
});

onMounted(() => {
	window.addEventListener("keydown", (e) => {
		if (e.key === "Alt") debugStore.setAltKeyHeld(true);
	});
	window.addEventListener("keyup", (e) => {
		if (e.key === "Alt") debugStore.setAltKeyHeld(false);
	});
});

onUnmounted(() => {
	window.removeEventListener("keydown", (e) => {
		if (e.key === "Alt") debugStore.setAltKeyHeld(true);
	});
	window.removeEventListener("keyup", (e) => {
		if (e.key === "Alt") debugStore.setAltKeyHeld(false);
	});
});
</script>
